// Hooks de lembretes — separados em 2 responsabilidades pra evitar race
// entre instâncias (consumeFiringReminders é destrutivo: lê + remove).
//
// - useFiringReminder: poll 30s + state do toast + foco automático.
//   USADO APENAS pelo <ReminderToastGlobal> no AppProviders. NUNCA
//   chamar em mais de 1 lugar — duplicaria poll e perderia disparos.
//
// - useReminderCycle: helper handleCycleReminder + reminderRefresh
//   trigger pro re-render dos badges na grid. Usado pela LiveScreen
//   (e qualquer outra tela que tenha cards de EPG com badge tier).
//   Não roda poll, só dispara mudança de estado quando user cicla tier.


import type { EpgProgram } from '@app-iptv/iptv-client'
import { setFocus } from '@app-iptv/ui'
import { useCallback, useEffect, useState } from 'react'

import { consumeFiringReminders, cycleReminder, type Reminder } from '../../lib/epg-reminders'

const POLL_INTERVAL_MS = 30 * 1000
const AUTO_DISMISS_MS = 30 * 1000
const FOCUS_TARGET = 'reminder-banner-go'

interface UseFiringReminderResult {
  /** Lembrete que acabou de disparar (mostra toast). null = sem toast. */
  firingReminder: Reminder | null
  /** Limpa o toast (chamar quando user clica "Assistir", "Espiar" ou
   *  "Dispensar"). */
  dismissReminder: () => void
}

export function useFiringReminder(): UseFiringReminderResult {
  const [firingReminder, setFiringReminder] = useState<Reminder | null>(null)

  // Poll 30s pra detectar quando lembrete começa
  useEffect(() => {
    const interval = setInterval(() => {
      const firing = consumeFiringReminders()
      if (firing.length > 0 && firing[0]) {
        setFiringReminder(firing[0])
        setTimeout(() => setFiringReminder(null), AUTO_DISMISS_MS)
      }
    }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  // Foco automático no botão "Assistir" quando o toast abre.
  // Retry pra Norigin registrar o Focusable após renderização.
  useEffect(() => {
    if (!firingReminder) return
    let tries = 0
    const tryFocus = () => {
      setFocus(FOCUS_TARGET)
      if (tries++ < 8) setTimeout(tryFocus, 80)
    }
    requestAnimationFrame(tryFocus)
  }, [firingReminder])

  const dismissReminder = useCallback(() => setFiringReminder(null), [])

  return { firingReminder, dismissReminder }
}

interface UseReminderCycleResult {
  /** Trigger de re-render. Bump quando o tier de algum lembrete muda
   *  pra forçar a grid re-renderizar o badge. Use `void reminderRefresh`
   *  no escopo render dos cards. */
  reminderRefresh: number
  /** Cicla tier off → 15min → 5min → agora → off pro programa dado. */
  handleCycleReminder: (
    channelId: string,
    channelName: string,
    program: EpgProgram | null,
  ) => void
}

export function useReminderCycle(): UseReminderCycleResult {
  const [reminderRefresh, setReminderRefresh] = useState(0)

  const handleCycleReminder = useCallback(
    (channelId: string, channelName: string, program: EpgProgram | null) => {
      if (!program) return
      cycleReminder({
        channelId,
        channelName,
        programTitle: program.title,
        startsAt: program.startTs * 1000,
      })
      setReminderRefresh((n) => n + 1)
    },
    [],
  )

  return { reminderRefresh, handleCycleReminder }
}
